import * as React from "react";
import { useTheme } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import moment from "moment";
import { StyledTableCell, StyledTableRow } from "./Styles/TableAndCellStyles";
import { withoutSymbolFormatter } from "utilities/currencyFormatter";
import ScoreChip from "components/ScoreChip";

function TablePaginationActions(props) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton
				onClick={(event) => onPageChange(event, 0)}
				disabled={page === 0}
				aria-label="first page"
			>
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton
				onClick={(event) => onPageChange(event, page - 1)}
				disabled={page === 0}
				aria-label="previous page"
			>
				{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton
				onClick={(event) => onPageChange(event, page + 1)}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="next page"
			>
				{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton
				onClick={(event) =>
					onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
				}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="last page"
			>
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

const tenantName = (tenant) => {
	if (!tenant) return "—";
	const name = `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim();
	return name || "—";
};

const tenantPhone = (tenant) => {
	if (!tenant) return "—";
	if (tenant.username) return tenant.username;
	if (tenant.phone_number != null) return String(tenant.phone_number);
	return "—";
};

const paymentDate = (row) => {
	const value = row.date_paid_display || row.date_paid || row.date_created;
	return value ? moment(value).format("DD MMM YYYY") : "—";
};

export default function PaymentsTable({
	rows,
	count = 0,
	page = 0,
	rowsPerPage = 20,
	onPageChange,
	onRowsPerPageChange,
}) {
	return (
		<TableContainer component={Paper}>
			<Table sx={{ minWidth: 700 }} aria-label="rental payments table">
				<TableHead>
					<TableRow>
						<StyledTableCell>Tenant Name</StyledTableCell>
						<StyledTableCell align="left">Score</StyledTableCell>
						<StyledTableCell align="left">Phone Number</StyledTableCell>
						<StyledTableCell align="left">Amount</StyledTableCell>
						<StyledTableCell align="left">Unit</StyledTableCell>
						<StyledTableCell align="left">Property</StyledTableCell>
						<StyledTableCell align="left">Payment Date</StyledTableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={7} align="center">
								No rental payments found.
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => (
							<StyledTableRow key={row.id}>
								<StyledTableCell component="th" scope="row">
									{tenantName(row.related_tenant)}
								</StyledTableCell>
								<StyledTableCell align="left">
									<ScoreChip creditScore={row.credit_score} />
								</StyledTableCell>
								<StyledTableCell align="left">
									{tenantPhone(row.related_tenant)}
								</StyledTableCell>
								<StyledTableCell align="left">
									{withoutSymbolFormatter(parseFloat(row.amount))}
								</StyledTableCell>
								<StyledTableCell align="left">
									{row.related_rental_unit?.unit_name || "—"}
								</StyledTableCell>
								<StyledTableCell align="left">
									{row.related_rental_unit?.related_rental?.rental_name || "—"}
								</StyledTableCell>
								<StyledTableCell align="left">{paymentDate(row)}</StyledTableCell>
							</StyledTableRow>
						))
					)}
				</TableBody>
			</Table>
			<TablePagination
				rowsPerPageOptions={[10, 20, 50, 100]}
				component="div"
				count={count}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={onPageChange}
				onRowsPerPageChange={onRowsPerPageChange}
				ActionsComponent={TablePaginationActions}
				labelDisplayedRows={({ from, to, count: total }) =>
					`${from}–${to} of ${total !== -1 ? total : `more than ${to}`}`
				}
			/>
		</TableContainer>
	);
}
